import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { ScanLine, Plus, Minus, CheckCircle2, AlertCircle, X, Printer } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import Barcode from "react-barcode";

// Helper: short beep using Web Audio
const beep = (ok = true) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "square";
    o.frequency.value = ok ? 1200 : 320;
    g.gain.value = 0.08;
    o.start();
    setTimeout(() => { o.stop(); ctx.close(); }, ok ? 90 : 200);
  } catch {}
};

export default function AdminScanMode() {
  const [input, setInput] = useState("");
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const focusInput = () => inputRef.current?.focus();

  useEffect(() => { focusInput(); }, []);

  // Keep input focused at all times (in case user clicks elsewhere)
  useEffect(() => {
    const handler = () => focusInput();
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, []);

  const lookup = async (sku) => {
    if (!sku.trim()) return;
    setError("");
    try {
      const r = await api.get(`/admin/parts/by-sku/${encodeURIComponent(sku.trim())}`);
      setCurrent(r.data);
      beep(true);
    } catch (err) {
      setCurrent(null);
      setError(err.response?.data?.detail || "Cod inexistent");
      beep(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    lookup(input);
    setInput("");
  };

  const adjust = async (delta) => {
    if (!current) return;
    try {
      const r = await api.post(`/admin/parts/${current.id}/adjust-stock`, { delta });
      const newStock = r.data.stock;
      const action = delta > 0 ? "intrare" : "iesire";
      setHistory((h) => [{
        id: Date.now(),
        name: current.name,
        sku: current.sku,
        delta,
        new_stock: newStock,
        time: new Date().toLocaleTimeString("ro-RO"),
      }, ...h.slice(0, 19)]);
      setCurrent({ ...current, stock: newStock });
      toast.success(`${current.name}: ${delta > 0 ? "+" : ""}${delta} (${action}) → stoc: ${newStock}`);
      beep(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare");
      beep(false);
    }
  };

  return (
    <div data-testid="scan-mode-page">
      <div className="flex items-center gap-3 mb-2">
        <ScanLine className="w-8 h-8 text-[#00E5FF]" />
        <h1 className="font-display text-4xl uppercase">Scan Mode</h1>
      </div>
      <p className="text-sm text-neutral-400 mb-2">Scaneaza un cod de bare cu Zebra. Apoi alegi +/- pentru stoc.</p>
      <a href="/admin/scan-test" className="text-xs uppercase tracking-widest text-[#00E5FF] hover:underline mb-6 inline-block">Scanner-ul nu raspunde? Verifica aici →</a>

      <form onSubmit={onSubmit} className="mb-6">
        <div className="relative">
          <ScanLine className="w-6 h-6 absolute left-4 top-1/2 -translate-y-1/2 text-[#00E5FF] kk-pulse" />
          <Input
            ref={inputRef}
            data-testid="scan-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Scaneaza sau introdu codul (Apasa Enter)"
            className="bg-neutral-900 border-2 border-[#00E5FF] focus:border-[#5CEEFF] rounded-none h-16 text-2xl pl-14 font-mono"
            autoFocus
          />
        </div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-2">Campul ramane focus-at automat. Doar scaneaza.</div>
      </form>

      {error && (
        <div className="bg-red-500/10 border border-red-500/40 p-5 mb-6 flex items-center gap-3" data-testid="scan-error">
          <AlertCircle className="w-6 h-6 text-red-400" />
          <div>
            <div className="font-display text-lg uppercase text-red-400">Cod inexistent</div>
            <div className="text-xs text-neutral-400 mt-1">{error}</div>
          </div>
        </div>
      )}

      {current && (
        <div className="bg-neutral-950 border-2 border-[#00E5FF] p-6 mb-6" data-testid="scan-result">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                <span className="text-xs uppercase tracking-widest text-green-400">Piesa identificata</span>
                <span className="font-mono text-xs text-neutral-500 ml-auto">{current.sku}</span>
              </div>
              <h2 className="font-display text-3xl uppercase">{current.name}</h2>
              <div className="flex flex-wrap gap-4 mt-2 text-sm text-neutral-400">
                {current.category && <span>Categorie: <span className="text-white">{current.category}</span></span>}
                {current.compatible_models && <span>Compat: <span className="text-white">{current.compatible_models}</span></span>}
                <span>Pret: <span className="text-[#00E5FF] font-medium">{current.price} RON</span></span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Stoc curent</div>
              <div className="font-display text-6xl text-white">{current.stock || 0}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mt-6">
            <Button onClick={() => adjust(1)} data-testid="scan-plus-1" className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/40 rounded-none h-14 text-base font-semibold">
              <Plus className="w-4 h-4 mr-1" /> +1
            </Button>
            <Button onClick={() => adjust(5)} className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/40 rounded-none h-14 text-base font-semibold">
              +5
            </Button>
            <Button onClick={() => adjust(10)} className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/40 rounded-none h-14 text-base font-semibold">
              +10
            </Button>
            <Button onClick={() => adjust(-1)} data-testid="scan-minus-1" className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/40 rounded-none h-14 text-base font-semibold">
              <Minus className="w-4 h-4 mr-1" /> -1
            </Button>
            <Button onClick={() => adjust(-5)} className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/40 rounded-none h-14 text-base font-semibold">
              -5
            </Button>
            <Button onClick={() => window.open(`/admin/piese/${current.id}/barcode`, "_blank")} variant="outline" className="border-neutral-700 text-neutral-200 rounded-none h-14 hover:text-[#00E5FF]">
              <Printer className="w-4 h-4 mr-1" /> Eticheta
            </Button>
          </div>

          {/* Barcode preview */}
          <div className="mt-6 bg-white p-4 flex justify-center">
            <Barcode value={current.sku} format="CODE128" width={2} height={50} fontSize={14} background="#ffffff" lineColor="#000000" />
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="bg-neutral-950 border border-neutral-800 p-5">
          <h3 className="font-display text-lg uppercase mb-3">Istoric sesiune ({history.length})</h3>
          <div className="space-y-1">
            {history.map((h) => (
              <div key={h.id} className="flex items-center gap-3 text-sm border-b border-neutral-900 py-1.5">
                <span className="text-[10px] text-neutral-500 w-16 font-mono">{h.time}</span>
                <span className={`text-xs font-bold w-10 ${h.delta > 0 ? "text-green-400" : "text-red-400"}`}>
                  {h.delta > 0 ? "+" : ""}{h.delta}
                </span>
                <span className="flex-1 truncate">{h.name}</span>
                <span className="font-mono text-[10px] text-neutral-500">{h.sku}</span>
                <span className="text-xs text-neutral-400">→ stoc: <span className="text-white font-medium">{h.new_stock}</span></span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
